import { LayoutTemplate } from 'lucide-react';
import type { WebsiteTemplateCatalogItemV3 } from '@dukanti/shared';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { useWebsiteT } from '../hooks/use-website-t';

export interface TemplateCardProps {
  readonly item: WebsiteTemplateCatalogItemV3;
  readonly onPreview: (item: WebsiteTemplateCatalogItemV3) => void;
  /** First use: the template has no saved draft yet. */
  readonly onUse: (item: WebsiteTemplateCatalogItemV3) => void;
  /** The template has a saved draft: open it again, exactly as it was left. */
  readonly onEdit: (item: WebsiteTemplateCatalogItemV3) => void;
  /** This card's Edit is opening its draft. */
  readonly opening?: boolean;
  /** Another card is opening: actions wait. */
  readonly disabled?: boolean;
}

/**
 * One template of the library: a storefront preview image, its business
 * category and a short description, colour swatches, "Preview demo" and the
 * main action. The frozen original design of a converted store is a neutral
 * tile with no thumbnail and no Preview.
 *
 * Epic #228 P3 badges: "Published" on the template customers see, "Draft saved"
 * on another template the merchant has edited, and "Unpublished changes" on
 * the live template only. A template with a saved draft offers "Edit" (its
 * draft opens as it was left); one without offers "Use template".
 */
export function TemplateCard({
  item,
  onPreview,
  onUse,
  onEdit,
  opening = false,
  disabled = false,
}: TemplateCardProps): React.JSX.Element {
  const { t } = useWebsiteT();
  const name = t(item.titleKey);
  // The accessible names put the template name in a positional placeholder
  // (FWT-p3-aria-positional-name). React escapes attributes itself, so the
  // name is not HTML-escaped a second time.
  const named = (key: string): string => t(key, { name, interpolation: { escapeValue: false } });
  const isLegacy = item.kind === 'legacy-frozen';
  const hasDraft = item.draft !== null;
  const unpublishedChanges = item.isLive && item.draft?.hasUnpublishedChanges === true;

  return (
    <article
      data-testid={`template-card-${item.slug}`}
      data-kind={item.kind}
      className="border-border bg-card shadow-admin-card flex flex-col overflow-hidden rounded-xl border"
    >
      <div className="bg-admin-surface-muted relative aspect-[4/3]">
        {item.thumbnailSrc === null ? (
          <div
            data-testid="template-card-neutral-tile"
            className="text-muted-foreground flex size-full items-center justify-center"
          >
            <LayoutTemplate aria-hidden="true" className="size-12" />
          </div>
        ) : (
          <img
            src={item.thumbnailSrc}
            alt={t('admin.websiteTemplates.editor.templateThumbnailAlt', { name })}
            loading="lazy"
            className="size-full object-cover object-top"
          />
        )}
        <div className="absolute start-3 top-3 flex flex-wrap gap-1.5">
          {item.isLive ? (
            <Badge variant="success" data-testid="template-card-live">
              {t('admin.websiteTemplates.gallery.badge.live')}
            </Badge>
          ) : hasDraft ? (
            <Badge variant="info" data-testid="template-card-draft-saved">
              {t('admin.websiteTemplates.gallery.badge.draftSaved')}
            </Badge>
          ) : null}
          {unpublishedChanges ? (
            <Badge variant="warning" data-testid="template-card-unpublished">
              {t('admin.websiteTemplates.editor.status.unpublishedChanges')}
            </Badge>
          ) : null}
          {item.kind === 'custom' ? (
            <Badge variant="warning">{t('admin.websiteTemplates.gallery.badge.custom')}</Badge>
          ) : null}
        </div>
      </div>

      <div className="flex flex-1 flex-col gap-3 p-4">
        <div className="space-y-1">
          <h2 className="font-display text-foreground text-lg font-semibold">{name}</h2>
          <p className="text-muted-foreground text-sm">{t(item.descriptionKey)}</p>
        </div>

        {item.swatches.length > 0 ? (
          <div
            role="group"
            aria-label={t('admin.websiteTemplates.gallery.swatchesLabel')}
            className="flex gap-1.5"
          >
            {item.swatches.map((color, slot) => (
              <span
                key={slot}
                aria-hidden="true"
                className="border-border size-5 rounded-full border"
                style={{ backgroundColor: color }}
              />
            ))}
          </div>
        ) : null}

        <div className="mt-auto flex flex-wrap gap-2 pt-1">
          {isLegacy ? null : (
            <Button
              type="button"
              variant="outline"
              aria-label={named('admin.websiteTemplates.gallery.action.previewDemoNamed')}
              onClick={() => onPreview(item)}
            >
              {t('admin.websiteTemplates.gallery.action.previewDemo')}
            </Button>
          )}
          {hasDraft ? (
            <Button
              type="button"
              aria-label={named('admin.websiteTemplates.gallery.action.editNamed')}
              aria-busy={opening || undefined}
              disabled={disabled || opening}
              onClick={() => onEdit(item)}
            >
              {opening
                ? t('admin.websiteTemplates.gallery.action.opening')
                : t('admin.websiteTemplates.gallery.action.edit')}
            </Button>
          ) : (
            <Button
              type="button"
              aria-label={named('admin.websiteTemplates.gallery.action.useTemplateNamed')}
              disabled={disabled}
              onClick={() => onUse(item)}
            >
              {t('admin.websiteTemplates.gallery.action.use')}
            </Button>
          )}
        </div>
      </div>
    </article>
  );
}
